export type EmailSignatureApplicationMode = 'new' | 'reply';

export interface ComposerEmailSignature {
  id: number;
  content: string;
  isDefault?: boolean | null;
  isAccountDefault?: boolean | null;
  isDefaultNew?: boolean | null;
  isDefaultReply?: boolean | null;
}

const SIGNATURE_BLOCK_PATTERN = /(?:\s|&nbsp;)*(?:<!-- experviser-signature:start[\s\S]*?<!-- experviser-signature:end -->)\s*$/;
const SIGNATURE_BOUNDARY = 'data-experviser-signature-boundary';
const SIGNATURE_BOUNDARY_BLOCK_PATTERN = new RegExp(
  `<div\\b(?=[^>]*\\b${SIGNATURE_BOUNDARY}=["']start["'])[^>]*>\\s*<\\/div>[\\s\\S]*?<div\\b(?=[^>]*\\b${SIGNATURE_BOUNDARY}=["']end["'])[^>]*>\\s*<\\/div>`,
  'i',
);

function escapePlainTextToHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function plainTextToEmailHtml(value: string): string {
  if (!value.trim()) return '';
  return escapePlainTextToHtml(value.replace(/\r\n?/g, '\n'))
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function stripAppliedEmailSignature(body: string): string {
  return body
    .replace(SIGNATURE_BOUNDARY_BLOCK_PATTERN, '')
    .replace(SIGNATURE_BLOCK_PATTERN, '')
    .trimEnd();
}

/** Remove editor-only boundary nodes before the message is saved or sent. */
export function stripEmailSignatureEditorMarkers(body: string): string {
  return body.replace(
    new RegExp(`<div\\b(?=[^>]*\\b${SIGNATURE_BOUNDARY}=["'](?:start|end)["'])[^>]*>\\s*<\\/div>`, 'gi'),
    '',
  );
}

/**
 * A Martina draft is also usable on its own (including direct send), so remove
 * its closing only at the boundary where an actual composer signature is
 * appended. Never run this on text the user has edited in the composer.
 */
export function trimMartinaDraftSignoffForSignature(
  draft: string,
  signature: ComposerEmailSignature | null | undefined,
): string {
  if (!signature?.content?.trim()) return draft;

  const visibleLines = (value: string) => value
    .replace(/<br\s*\/?\s*>|<\/(?:p|div)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const signatureLines = visibleLines(signature.content).map((line) => line.toLocaleLowerCase());
  const closing = /(?:^|\r?\n|<p(?:\s[^>]*)?>|<div(?:\s[^>]*)?>|<br\s*\/?\s*>)\s*(?:best(?: regards)?|kind regards|warm regards|regards|sincerely|cordiali saluti|un saluto)[,.!]?(?=\s|<|$)/gi;
  let candidate: RegExpExecArray | null;
  let matchedStart = -1;
  while ((candidate = closing.exec(draft))) {
    const lines = visibleLines(draft.slice(candidate.index));
    const tail = lines.join(' ');
    const match = tail.match(/^(?:best(?: regards)?|kind regards|warm regards|regards|sincerely|cordiali saluti|un saluto)[,.!]?\s+([\p{L}][\p{L}.'’ -]{0,59})$/iu);
    if (!match) continue;
    const name = match[1].trim().toLocaleLowerCase();
    // The sign-off belongs to this sender only if the configured signature
    // starts a line with the same name (possibly followed by a surname).
    if (!signatureLines.some((line) => line === name || line.startsWith(`${name} `))) continue;
    matchedStart = candidate.index;
  }
  if (matchedStart < 0) return draft;
  const prefix = draft.slice(0, matchedStart).trimEnd();
  // A closing can live after <br> inside the same paragraph as the body.
  // Preserve that paragraph instead of leaving malformed HTML behind.
  const openBlocks: string[] = [];
  for (const tag of prefix.matchAll(/<(\/)?(p|div)\b[^>]*>/gi)) {
    if (tag[1]) {
      if (openBlocks[openBlocks.length - 1] === tag[2].toLowerCase()) openBlocks.pop();
    } else {
      openBlocks.push(tag[2].toLowerCase());
    }
  }
  return prefix + openBlocks.reverse().map((tag) => `</${tag}>`).join('');
}

export function applyEmailSignature(
  body: string,
  signature: ComposerEmailSignature | null | undefined,
): string {
  const bodyWithoutPreviousSignature = stripAppliedEmailSignature(body);
  if (!signature?.content?.trim()) return bodyWithoutPreviousSignature;

  const signatureBlock = [
    `<div ${SIGNATURE_BOUNDARY}="start" data-signature-id="${signature.id}"></div>`,
    signature.content,
    `<div ${SIGNATURE_BOUNDARY}="end" data-signature-id="${signature.id}"></div>`,
  ].join('');
  // TipTap adds a trailing editable paragraph after the atomic end boundary.
  // Replacing in place keeps that paragraph (and any text written after the
  // signature) in place instead of accumulating blank lines on each change.
  if (SIGNATURE_BOUNDARY_BLOCK_PATTERN.test(body)) {
    return body.replace(SIGNATURE_BOUNDARY_BLOCK_PATTERN, () => signatureBlock);
  }
  return bodyWithoutPreviousSignature + signatureBlock;
}

export function selectDefaultEmailSignature(
  signatures: ComposerEmailSignature[],
  mode: EmailSignatureApplicationMode,
): ComposerEmailSignature | null {
  const accountDefault = signatures.find((signature) => (
    mode === 'reply'
      ? signature.isDefaultReply
      : signature.isDefaultNew
  ));

  return accountDefault
    || signatures.find((signature) => signature.isAccountDefault)
    || signatures.find((signature) => signature.isDefault)
    || null;
}
